import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Autocomplete,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Inventory2 as RepackIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import Layout from '../../components/layout/Layout';
import productService from '../../services/productService';
import unitService from '../../services/unitService';

const COLUMN_KEYS = ['name', 'uom', 'unit', 'mrp', 'retail', 'wSales'];

const createEmptyRow = (id) => ({
  id,
  name: '',
  unitId: null,
  unit: 0,
  mrp: 0,
  retail: 0,
  wSales: 0,
});

const RepackingManagementPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [parentProduct, setParentProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);
  const [rows, setRows] = useState([createEmptyRow(1)]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const nextIdRef = useRef(2);

  const nameRefs = useRef([]);
  const uomRefs = useRef([]);
  const unitRefs = useRef([]);
  const mrpRefs = useRef([]);
  const retailRefs = useRef([]);
  const wSalesRefs = useRef([]);
  const [openUomIdx, setOpenUomIdx] = useState(null);

  const handleTabOrderEnterRef = useRef(null);

  const loadProducts = useCallback(async () => {
    try {
      const res = await productService.getAllProductsForManagement(0, 500, 'name', 'asc');
      const list = res?.content || res || [];
      setProducts(list.filter((p) => !p.parentProductId));
    } catch (err) {
      console.error('Error loading products:', err);
      enqueueSnackbar('Failed to load products', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  const loadUnits = useCallback(async () => {
    try {
      const res = await unitService.getAllUnits({ page: 0, size: 100 });
      const list = res?.content ?? res ?? [];
      setUnits(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error loading units:', err);
      enqueueSnackbar('Failed to load units', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  const loadRepackedVariants = useCallback(async () => {
    if (!parentProduct?.id) return;
    setLoading(true);
    try {
      const variants = await productService.getRepackedVariants(parentProduct.id);
      if (variants?.length > 0) {
        const mapped = variants.map((v, i) => ({
          id: 1000 + i,
          name: v.name || '',
          unitId: v.unit?.id || null,
          unit: parseFloat(v.parentConversionRatio) || 0,
          mrp: parseFloat(v.mrp) || 0,
          retail: parseFloat(v.sellingPrice) || 0,
          wSales: parseFloat(v.wholesalePrice) || 0,
          existingId: v.id,
        }));
        setRows([...mapped, createEmptyRow(nextIdRef.current++)]);
      } else {
        setRows([createEmptyRow(nextIdRef.current++)]);
      }
    } catch (err) {
      console.error('Error loading repacked variants:', err);
      enqueueSnackbar('Failed to load repacked variants', { variant: 'error' });
      setRows([createEmptyRow(nextIdRef.current++)]);
    } finally {
      setLoading(false);
    }
  }, [parentProduct?.id, enqueueSnackbar]);

  useEffect(() => {
    loadProducts();
    loadUnits();
  }, [loadProducts, loadUnits]);

  useEffect(() => {
    if (parentProduct?.id) {
      loadRepackedVariants();
    } else {
      setRows([createEmptyRow(1)]);
      nextIdRef.current = 2;
    }
  }, [parentProduct?.id, loadRepackedVariants]);

  const focusRef = useCallback((key, rowIndex) => {
    const refMap = {
      name: nameRefs,
      uom: uomRefs,
      unit: unitRefs,
      mrp: mrpRefs,
      retail: retailRefs,
      wSales: wSalesRefs,
    };
    const refs = refMap[key];
    if (refs && refs.current[rowIndex]) {
      refs.current[rowIndex].focus?.();
      // If focusing UOM, trigger it to open
      if (key === 'uom') {
        setOpenUomIdx(rowIndex);
      }
    }
  }, []);

  const addNewRow = useCallback(() => {
    setRows((prev) => {
      const newLen = prev.length;
      setTimeout(() => {
        if (nameRefs.current[newLen]) {
          nameRefs.current[newLen].focus?.();
        }
      }, 80);
      return [...prev, createEmptyRow(nextIdRef.current++)];
    });
  }, []);

  const handleTabOrderEnter = useCallback(
    (rowIndex, currentKey) => {
      const idx = COLUMN_KEYS.indexOf(currentKey);
      if (idx < 0) return;
      if (idx + 1 < COLUMN_KEYS.length) {
        const nextKey = COLUMN_KEYS[idx + 1];
        setTimeout(() => focusRef(nextKey, rowIndex), 50);
      } else {
        addNewRow();
      }
    },
    [focusRef, addNewRow]
  );

  handleTabOrderEnterRef.current = handleTabOrderEnter;

  const deleteRow = useCallback((rowIndex) => {
    setRows((prev) => {
      if (prev.length <= 1) return [createEmptyRow(nextIdRef.current++)];
      return prev.filter((_, i) => i !== rowIndex);
    });
  }, []);

  const updateRow = useCallback((rowIndex, field, value) => {
    setRows((prev) =>
      prev.map((row, i) =>
        i === rowIndex ? { ...row, [field]: value } : row
      )
    );
  }, []);

  const handleSave = async () => {
    if (!parentProduct?.id) {
      enqueueSnackbar('Please select a parent product', { variant: 'warning' });
      return;
    }
    const validRows = rows.filter(
      (r) =>
        (r.name?.trim() || '').length > 0 &&
        r.unitId != null &&
        parseFloat(r.unit) > 0 &&
        parseFloat(r.retail) >= 0
    );
    if (validRows.length === 0) {
      enqueueSnackbar('Add at least one repacked variant with Name, UOM, Unit, and Retail', {
        variant: 'warning',
      });
      return;
    }
    setSaving(true);
    try {
      let created = 0;
      let updated = 0;
      for (const row of validRows) {
        const payload = {
          name: (row.name || '').trim(),
          unitId: row.unitId,
          parentConversionRatio: parseFloat(row.unit),
          sellingPrice: parseFloat(row.retail) || 0,
          purchasePrice: 0, // Removed from UI
          wholesalePrice: parseFloat(row.wSales) || 0,
          minSalePrice: parseFloat(row.retail) || 0, // Default to retail if not specified
          mrp: parseFloat(row.mrp) || 0,
        };

        if (row.existingId) {
          await productService.updateRepackedVariant(row.existingId, payload);
          updated++;
        } else {
          await productService.createRepackedVariant(parentProduct.id, payload);
          created++;
        }
      }
      enqueueSnackbar(`Successfully saved configuration (${created} new, ${updated} updated)`, {
        variant: 'success'
      });
      loadRepackedVariants();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to save';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setParentProduct(null);
    setRows([createEmptyRow(1)]);
    nextIdRef.current = 2;
    enqueueSnackbar('Cancelled', { variant: 'info' });
  };

  const parentUnitLabel = parentProduct?.unit?.abbreviation || parentProduct?.unit?.name || 'kg';

  return (
    <Layout>
      <Box sx={{
        p: { xs: 2, md: 3 },
        maxWidth: '1200px',
        margin: '0 auto',
        fontFamily: "'Roboto', sans-serif",
        color: '#1a1a1a',
        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
          display: 'none',
        },
        '& input[type=number]': {
          MozAppearance: 'textfield',
        },
      }}>
        {/* Compact Premium Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'flex-start' }}>
          <RepackIcon sx={{ fontSize: 28, color: '#1a237e' }} />
          <Typography variant="h5" fontWeight="800" sx={{
            color: '#1a237e',
            letterSpacing: '-0.5px'
          }}>
            Repacking Management
          </Typography>
        </Box>

        {/* Unified Action Area - More Compact */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Step 1: Selection */}
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: '300px' }}>
              <Typography variant="caption" sx={{
                fontWeight: '800',
                color: '#311b92',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                mb: 0.5,
                display: 'block'
              }}>
                Select Bulk Product
              </Typography>
              <Autocomplete
                options={products}
                getOptionLabel={(opt) => (typeof opt === 'object' ? `${opt.name || ''} (${opt.sku || ''})` : '')}
                value={parentProduct}
                onChange={(_, v) => setParentProduct(v)}
                onInputChange={(_, v) => setProductSearch(v)}
                inputValue={productSearch}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    placeholder="Search product..."
                    variant="standard"
                    sx={{
                      '& .MuiInput-root': {
                        fontSize: '1rem',
                        fontWeight: '600',
                        padding: '4px 0',
                        '&:before': { borderBottom: '1px solid rgba(0,0,0,0.1)' },
                      }
                    }}
                  />
                )}
              />
            </Box>

            {parentProduct && (
              <Box sx={{
                display: 'flex',
                gap: 2,
                bgcolor: 'rgba(49, 27, 146, 0.04)',
                p: 1.5,
                px: 2,
                borderRadius: '8px',
                border: '1px solid rgba(49, 27, 146, 0.1)'
              }}>
                <Box>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Main Unit</Typography>
                  <Typography variant="body2" fontWeight="700" color="#311b92">{parentUnitLabel}</Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ borderRightWidth: 1 }} />
                <Box>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Category</Typography>
                  <Typography variant="body2" fontWeight="700" color="#311b92">{parentProduct.category?.name || '-'}</Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Step 2: Grid */}
          {parentProduct && (
            <Box sx={{ animation: 'slideUp 0.4s ease-out' }}>
              <style>{`
                @keyframes slideUp {
                  from { opacity: 0; transform: translateY(10px); }
                  to { opacity: 1; transform: translateY(0); }
                }
              `}</style>

              <Typography variant="caption" sx={{
                fontWeight: '800',
                color: '#311b92',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                mb: 1,
                mt: 1,
                display: 'block'
              }}>
                Define Repacked Variants
              </Typography>

              <Box sx={{
                bgcolor: '#fff',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                border: '1px solid rgba(0,0,0,0.06)'
              }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#fcfcfc' }}>
                      <TableCell sx={{ color: '#666', fontWeight: '700', fontSize: '0.7rem', width: 40 }}>#</TableCell>
                      <TableCell sx={{ color: '#1a237e', fontWeight: '800', fontSize: '0.75rem' }}>Variant Name</TableCell>
                      <TableCell sx={{ color: '#1a237e', fontWeight: '800', fontSize: '0.75rem' }}>UOM</TableCell>
                      <TableCell sx={{ color: '#1a237e', fontWeight: '800', fontSize: '0.75rem' }} align="right">Unit (Qty)</TableCell>
                      <TableCell sx={{ color: '#1a237e', fontWeight: '800', fontSize: '0.75rem' }} align="right">MRP</TableCell>
                      <TableCell sx={{ color: '#1a237e', fontWeight: '800', fontSize: '0.75rem' }} align="right">Retail Rate</TableCell>
                      <TableCell sx={{ color: '#1a237e', fontWeight: '800', fontSize: '0.75rem' }} align="right">Wholesale Rate</TableCell>
                      <TableCell align="center" width={40}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, index) => (
                      <TableRow key={row.id} sx={{ '&:hover': { bgcolor: '#f9f9ff' } }}>
                        <TableCell sx={{ color: '#ddd', fontWeight: '700', py: 1 }}>{index + 1}</TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <TextField
                            fullWidth
                            variant="standard"
                            placeholder="Variant Name"
                            value={row.name}
                            onChange={(e) => updateRow(index, 'name', e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleTabOrderEnterRef.current?.(index, 'name'))}
                            inputRef={(el) => (nameRefs.current[index] = el)}
                            InputProps={{ disableUnderline: true, sx: { fontWeight: '600', fontSize: '0.85rem' } }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <FormControl variant="standard" fullWidth>
                            <Select
                              open={openUomIdx === index}
                              onOpen={() => setOpenUomIdx(index)}
                              onClose={() => setOpenUomIdx(null)}
                              inputRef={(el) => (uomRefs.current[index] = el)}
                              value={row.unitId ?? ''}
                              onChange={(e) => {
                                updateRow(index, 'unitId', e.target.value || null);
                                // Optional: Auto-focus next field after selection if you want, 
                                // but user said "press enter means then only it move"
                              }}
                              onKeyDown={(e) => {
                                // If the menu is open, let MUI handle Enter for selection.
                                // If the menu is closed and we press Enter, move to next.
                                if (e.key === 'Enter') {
                                  if (!openUomIdx) {
                                    e.preventDefault();
                                    handleTabOrderEnterRef.current?.(index, 'uom');
                                  } else {
                                    // Menu is open, do nothing special, let selection happen
                                  }
                                }
                              }}
                              displayEmpty
                              disableUnderline
                              sx={{ fontWeight: '500', fontSize: '0.85rem', color: row.unitId ? 'inherit' : '#aaa' }}
                            >
                              <MenuItem value="">Select Unit</MenuItem>
                              {units.map((u) => (
                                <MenuItem key={u.id} value={u.id}>{u.name || u.abbreviation}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>
                          <TextField
                            fullWidth
                            variant="standard"
                            type="number"
                            placeholder="0.00"
                            value={row.unit || ''}
                            onChange={(e) => updateRow(index, 'unit', e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleTabOrderEnterRef.current?.(index, 'unit'))}
                            onWheel={(e) => e.target.blur()}
                            inputRef={(el) => (unitRefs.current[index] = el)}
                            InputProps={{ disableUnderline: true, sx: { fontWeight: '700', fontSize: '0.85rem' }, inputProps: { style: { textAlign: 'right' } } }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>
                          <TextField
                            fullWidth
                            variant="standard"
                            type="number"
                            placeholder="0"
                            value={row.mrp || ''}
                            onChange={(e) => updateRow(index, 'mrp', e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleTabOrderEnterRef.current?.(index, 'mrp'))}
                            onWheel={(e) => e.target.blur()}
                            inputRef={(el) => (mrpRefs.current[index] = el)}
                            InputProps={{ disableUnderline: true, sx: { fontWeight: '700', fontSize: '0.85rem', color: '#2e7d32' }, inputProps: { style: { textAlign: 'right' } } }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>
                          <TextField
                            fullWidth
                            variant="standard"
                            type="number"
                            placeholder="0"
                            value={row.retail || ''}
                            onChange={(e) => updateRow(index, 'retail', e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleTabOrderEnterRef.current?.(index, 'retail'))}
                            onWheel={(e) => e.target.blur()}
                            inputRef={(el) => (retailRefs.current[index] = el)}
                            InputProps={{ disableUnderline: true, sx: { fontWeight: '700', fontSize: '0.85rem', color: '#c62828' }, inputProps: { style: { textAlign: 'right' } } }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>
                          <TextField
                            fullWidth
                            variant="standard"
                            type="number"
                            placeholder="0"
                            value={row.wSales || ''}
                            onChange={(e) => updateRow(index, 'wSales', e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleTabOrderEnterRef.current?.(index, 'wSales'))}
                            onWheel={(e) => e.target.blur()}
                            inputRef={(el) => (wSalesRefs.current[index] = el)}
                            InputProps={{ disableUnderline: true, sx: { fontWeight: '700', fontSize: '0.85rem', color: '#1565c0' }, inputProps: { style: { textAlign: 'right' } } }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1 }}>
                          <IconButton size="small" onClick={() => deleteRow(index)} color="error" sx={{ opacity: 0.3, '&:hover': { opacity: 1 } }}>
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>

              {/* Compact Bottom Actions */}
              <Box sx={{
                mt: 3,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2
              }}>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
                    Conversion: 1 Unit = <b>Qty</b> of {parentUnitLabel} • Auto-generated Barcodes.
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Button
                    size="small"
                    variant="text"
                    color="inherit"
                    sx={{ px: 2, borderRadius: '4px', fontWeight: '700', fontSize: '0.75rem' }}
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      bgcolor: '#311b92',
                      '&:hover': { bgcolor: '#1a237e' },
                      px: 3,
                      py: 0.75,
                      borderRadius: '4px',
                      fontWeight: '700',
                      fontSize: '0.75rem',
                      boxShadow: 'none'
                    }}
                  >
                    Save Configuration
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Layout>
  );
};

export default RepackingManagementPage;
